import { useDispatch, useSelector } from 'react-redux';
import AwesomeDebouncePromise from 'awesome-debounce-promise';
import { addToTrigger } from '../../features/quote/data/WatchTriggers';
import { actions, getCurrentStep, getQuote } from '../../features/quote/QuoteSlice';
import {
  WatchTriggerActionInterface, Field, WatchTriggerInterface,
} from '../../types';
import { WatchTriggerActionType } from '../../types/enum';
import KciApi from '../api/KCI';

const useActions = (triggers: any) => {
  const quoteData = useSelector(getQuote);
  const dispatch = useDispatch();
  const processNewQuestionControl = (options: any) => {
    let currentQuestionId = null;
    const newOptions = options;
    const optionOptions: any = {};

    dispatch(actions.setIsLoading(true));
    for (let i = 0; i < newOptions.length; i += 1) {
      optionOptions[`quote_prop_${newOptions[i].question_id}`] = optionOptions[`quote_prop_${newOptions[i].question_id}`] || [{
        value: 'false',
        text: 'Select an answer...',
        disabled: true,
      }];
      newOptions[i] = {
        ...newOptions[i],
        value: `quote_prop_${newOptions[i].question_id}-${newOptions[i].value}`,
      };
      optionOptions[`quote_prop_${newOptions[i].question_id}`].push(newOptions[i]);
      currentQuestionId = newOptions[i].question_id;
    }
    Object.keys(optionOptions).forEach((question: string) => {
      dispatch(actions.updateOptionsForControl({
        formId: question,
        options: optionOptions[question],
      }));
    });
    dispatch(actions.setIsLoading(false));
    dispatch(actions.setIsFetching(false));
  };

  const runAction = (
    trigger: string,
    action: WatchTriggerActionInterface,
    value: string | boolean | Date,
    index?: number,
  ) => {
    let savedField = null;
    switch (action.type) {
      case WatchTriggerActionType.Show:
        savedField = quoteData.showData[action.target[0]];
        action.target.forEach((formId: string) => {
          dispatch(actions.removeFromDisplayQueue(formId));
        });
        dispatch(actions.addToDisplayQueue([
          {
            index,
            step: savedField
              ? [savedField.step, savedField.innerStep]
              : [quoteData.step.step, quoteData.step.innerStep],
            trigger,
            fields:
              action.target.map((target: string) => {
                dispatch(actions.removeFromRemoveQueue(target));
                return {
                  ...quoteData.showData[target].field,
                  disabled: false,
                };
              }),
          },
        ]));
        break;
      case WatchTriggerActionType.Remove:
      case WatchTriggerActionType.Hide: {
        savedField = quoteData.showData[action.target[0]];
        const renderStep = savedField
          ? [savedField.step, savedField.innerStep]
          : [quoteData.step.step, quoteData.step.innerStep];

        const showingFilters = Object.keys(quoteData.data).filter((formId: any) => {
          if (formId !== trigger && value !== true) {
            if (Object.prototype.hasOwnProperty.call(triggers, formId)) {
              const showTriggers = triggers[formId].filter(
                (watchTrig: WatchTriggerInterface) => {
                  if (watchTrig.on.type === WatchTriggerActionType.Show
                        || watchTrig.off.type === WatchTriggerActionType.Show) {
                    if (watchTrig.values?.includes(quoteData.data[formId]) || watchTrig.values?.includes('all')
                    ) {
                      if (watchTrig.on.target.filter(
                        (element) => action.target.includes(element),
                      ).length > 0 && action.type !== WatchTriggerActionType.Remove) {
                        return true;
                      }
                    }
                  }
                  return false;
                },
              );
              return showTriggers.length > 0;
            }
          }
          return false;
        });
        if (showingFilters.length === 0) {
          action.target.forEach((formId: string) => {
            if (renderStep[1] !== quoteData.step.innerStep) {
              dispatch(actions.removeFromDisplayQueue(formId));
            }
          });
          dispatch(actions.addToRemoveQueue(
            {
              step: renderStep,
              trigger,
              fields: action.target,
            },
          ));
        }
        break;
      }
      case WatchTriggerActionType.AddAttribute:
        dispatch(actions.addToPreUpdateQueue(action));
        break;
      case WatchTriggerActionType.RemoveAttribute:
        dispatch(actions.addToPreUpdateQueue(action));
        break;
      case WatchTriggerActionType.AddOption:
        dispatch(actions.addOptionForControl({
          formId: action.target,
          options: action.options,
        }));
        break;
      case WatchTriggerActionType.RemoveOption:
        dispatch(actions.removeOptionFromControl({
          formId: action.target,
          options: action.options,
        }));
        break;
      case WatchTriggerActionType.LoadFields:
        if (action.data) {
          if (Object.prototype.hasOwnProperty.call(window.kbSchema.data, action.data)) {
            dispatch(actions.setIsLoading(true));

            fetch(window.kbSchema.data[action.data].url, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8',
              },
              body: new URLSearchParams(window.kbSchema.data[action.data].data),
            }).then((response) => response.json())
              .then((loadedFields) => {
                // Once we have loaded files we should create a watch trigger to remove them
                // On the opposite of this value
                // If target add to loaded fields array
                action.target.forEach((target:string) => {
                  if (quoteData.showData[target]) {
                    loadedFields.push(quoteData.showData[target].field);
                  }
                });
                addToTrigger(trigger, {
                  values: [value],
                  on: {
                    type: WatchTriggerActionType.None,
                    target: [],
                  },
                  off: {
                    type: WatchTriggerActionType.Remove,
                    target: loadedFields.filter(
                      (f: Field) => !!f.formId,
                    ).map((f: Field) => f.formId),
                  },
                  index: index || 0,
                });
                loadedFields.forEach((field: Field, fieldIndex: number) => {
                  dispatch(actions.addToShowData({
                    field: {
                      ...field,
                      type: field.formId && field.formId.indexOf('postcode') > -1 ? 'postcode' : field.type,
                    },
                    step: quoteData.step.step,
                    fieldIndex: fieldIndex + 1,
                    innerStep: quoteData.step.innerStep,
                  }));
                  if (field.options) {
                    dispatch(actions.updateOptionsForControl({
                      formId: field.formId,
                      options: field.options,
                    }));
                  }
                });
                dispatch(actions.addToDisplayQueue([{
                  step: [quoteData.step.step, quoteData.step.innerStep],
                  index: (index || 0) + 1,
                  trigger,
                  fields: loadedFields.map((loadedField: Field) => ({
                    ...loadedField,
                    type: loadedField.formId && loadedField.formId.indexOf('postcode') > -1 ? 'postcode' : loadedField.type,
                  })),
                }]));
                dispatch(actions.setIsLoading(false));
              })
              .catch((e) => {
                dispatch(actions.setIsLoading(false));
              });
          }
        }
        break;
      case WatchTriggerActionType.GetPrice:
        if (action.data) {
          if (Object.prototype.hasOwnProperty.call(window.kbSchema, action.data)) {
            dispatch(actions.setIsLoading(true));
            fetch(window.kbSchema.data[action.data].url, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8',
              },
              body: new URLSearchParams(window.kbSchema.data[action.data].data),
            }).then((response) => response.json())
              .then(() => {
                dispatch(actions.setIsLoading(false));
              })
              .catch((e) => {
                dispatch(actions.setIsLoading(false));
              });
          }
        }
        break;
      case WatchTriggerActionType.ProcessQuote:
        dispatch(actions.setIsFetching(true));
        AwesomeDebouncePromise(
          async () => KciApi.getFormQuestions(quoteData.ptc, value, trigger),
          500,
        )()
          .then((response: any) => {
            if (response.data.effected_options) {
              processNewQuestionControl(response.data.effected_options);
            } else {
              dispatch(actions.setIsFetching(false));
              dispatch(actions.setIsLoading(false));
            }
          });
        break;
      case WatchTriggerActionType.UpdateTransaction:
        // We only want to update transactions on none cloned fields
        dispatch(actions.setIsFetching(true));
        KciApi.updateTransaction(quoteData.ptc, value)
          .then(() => {
            KciApi.getFormQuestions(quoteData.ptc, value, trigger).then((response: any) => {
              if (response.data.effected_options) {
                processNewQuestionControl(response.data.effected_options);
              } else {
                dispatch(actions.setIsFetching(false));
                dispatch(actions.setIsLoading(false));
              }
            });
          });
        break;
      default:
        break;
    }
  };
  return { runAction };
};

export default useActions;
